<template>
  <div
    class="bg-fill-brand-secondary border-[3px] border-border-brand rounded-3xl shadow-[1px_3px_21px_0px_rgba(0,0,0,0.1)] w-[312px] px-6 py-[30px] overflow-hidden flex flex-col gap-4 text-left"
  >
    <div class="flex flex-col gap-4 w-full">
      <!--
        Title (Freeman font via font-serif), clamped to 2 lines with fixed
        height. Uses the theme-adaptive `text-text` token (dark on the light
        theme's pink card → 10.9:1 contrast). The previous `text-text-primary`
        was a no-op class (no such token in the design system), so the title
        fell back to an inherited colour that was unreadable on the new light
        theme.
      -->
      <p
        class="text-text text-xl font-serif uppercase leading-[26px] line-clamp-2 h-[52px]"
      >
        {{ offer.name }}
      </p>

      <div class="relative h-[180px] flex items-center justify-center">
        <img
          v-if="offer.image_url"
          :src="offer.image_url"
          :alt="offer.name"
          class="max-w-full max-h-full object-contain rounded-2xl"
        />
        <div v-else class="w-full h-full flex items-center justify-center">
          <Icon
            icon="mdi:tag-outline"
            class="text-icon-secondary text-4xl"
            aria-hidden="true"
          />
        </div>
      </div>

      <!-- CTA area (only this is clickable) -->
      <div class="w-full">
        <!--
          The CTA label + icons inherit `ctaContentClass`, which picks a
          theme-adaptive text colour with proper contrast for the active CTA
          background: white (`text-text-on-brand`) on the red `fill-brand`
          "Claimed" state (5.4:1), dark (`text-text`) on the light "Sold Out"
          (`fill-disabled`, 8.3:1) and pink `fill-brand-secondary` (7.5:1)
          states. The previous `text-text-primary` / `text-icon-primary` were
          no-op classes (no such tokens), leaving white-ish inherited text that
          was unreadable on the light theme's pale CTA fills.
        -->
        <button
          type="button"
          class="rounded-full h-10 w-full flex items-center justify-center px-6 py-1 gap-2 cursor-pointer transition-colors"
          :class="[ctaClasses, ctaContentClass]"
          :aria-label="cardAriaLabel"
          @click="emit('cta-click')"
        >
          <Icon
            v-if="hasClaimed"
            icon="mdi:check-circle"
            class="size-4"
            aria-hidden="true"
          />
          <span class="text-sm leading-[20px] font-medium text-center">
            {{ ctaLabel }}
          </span>
          <Icon
            v-if="isGated && !hasClaimed && !isSoldOut"
            icon="mdi:lock"
            class="size-4"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>

    <!-- Card footer -->
    <div class="flex items-center justify-between w-full">
      <!-- Left: gating criteria indicators (star badges, stacking) -->
      <div class="flex items-center">
        <template v-if="gatingCriterionIcons.length">
          <div
            v-for="(item, idx) in gatingCriterionIcons"
            :key="item.icon"
            class="relative size-[35px] shrink-0 flex items-center justify-center backdrop-blur-[2px] text-fill-brand"
            :class="{ '-ml-2': idx > 0, 'opacity-40': !item.qualified }"
            :style="{ zIndex: gatingCriterionIcons.length - idx }"
          >
            <svg
              class="absolute inset-0 size-full"
              viewBox="0 0 31.15 29.92"
              fill="none"
            >
              <path
                d="M13.44 0.9C14.61-0.3 16.54-0.3 17.71 0.9L22.54 5.8C22.77 6.04 23.04 6.23 23.34 6.38L29.49 9.45C30.99 10.2 31.59 12.03 30.81 13.52L27.64 19.62C27.49 19.92 27.38 20.23 27.34 20.56L26.31 27.36C26.07 29.02 24.51 30.15 22.85 29.88L16.07 28.75C15.74 28.69 15.41 28.69 15.08 28.75L8.3 29.88C6.64 30.15 5.09 29.02 4.84 27.36L3.82 20.56C3.77 20.23 3.66 19.92 3.51 19.62L0.34 13.52C-0.43 12.03 0.16 10.2 1.66 9.45L7.81 6.38C8.11 6.23 8.38 6.04 8.61 5.8L13.44 0.9Z"
                fill="currentColor"
                fill-opacity="0.3"
              />
            </svg>
            <!-- Criterion icon: use image override or iconify -->
            <img
              v-if="iconImageOverrides[item.icon]"
              :src="iconImageOverrides[item.icon]"
              alt=""
              class="relative size-6 object-contain"
            />
            <!--
              Qualified criteria use the theme-adaptive `text-icon-emphasis`
              token (dark on the light theme, 10.9:1 on the pink card); the
              previous `text-icon-primary` was a no-op class that fell back to
              the parent's brand red (2.8:1, unreadable on the light theme).
              Unqualified criteria keep `text-icon-secondary` and the wrapper's
              `opacity-40` as the intentional dimmed-state cue.
            -->
            <Icon
              v-else
              :icon="item.icon"
              class="relative size-5"
              :class="
                item.qualified ? 'text-icon-emphasis' : 'text-icon-secondary'
              "
            />
          </div>
        </template>
        <!-- "Open to everyone" indicator when no gating criteria -->
        <div
          v-else
          class="relative size-[35px] shrink-0 flex items-center justify-center backdrop-blur-[2px] text-fill-brand"
        >
          <svg
            class="absolute inset-0 size-full"
            viewBox="0 0 31.15 29.92"
            fill="none"
          >
            <path
              d="M13.44 0.9C14.61-0.3 16.54-0.3 17.71 0.9L22.54 5.8C22.77 6.04 23.04 6.23 23.34 6.38L29.49 9.45C30.99 10.2 31.59 12.03 30.81 13.52L27.64 19.62C27.49 19.92 27.38 20.23 27.34 20.56L26.31 27.36C26.07 29.02 24.51 30.15 22.85 29.88L16.07 28.75C15.74 28.69 15.41 28.69 15.08 28.75L8.3 29.88C6.64 30.15 5.09 29.02 4.84 27.36L3.82 20.56C3.77 20.23 3.66 19.92 3.51 19.62L0.34 13.52C-0.43 12.03 0.16 10.2 1.66 9.45L7.81 6.38C8.11 6.23 8.38 6.04 8.61 5.8L13.44 0.9Z"
              fill="currentColor"
              fill-opacity="0.3"
            />
          </svg>
          <Icon
            icon="mdi:earth"
            class="relative size-5 text-icon-emphasis"
            aria-hidden="true"
          />
        </div>
      </div>

      <!-- Right: info tooltip + admin dropdown -->
      <div class="flex items-center gap-2">
        <InfoTooltip side="bottom">
          <template #trigger>
            <button
              type="button"
              :aria-label="t('channel.offers.card.info')"
              class="size-11 bg-fill-brand-secondary backdrop-blur-[17.5px] rounded-full flex items-center justify-center cursor-pointer"
            >
              <Icon
                icon="material-symbols:info-outline"
                class="text-text size-5"
                aria-hidden="true"
              />
            </button>
          </template>
          <p>{{ tooltipLabel }}</p>
        </InfoTooltip>
        <OfferItemDropdown
          v-if="canManageOffers"
          :offer-id="offer.id"
          @deleted="emit('deleted')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { Icon } from "@iconify/vue/dist/iconify.js";
import OfferItemDropdown from "./offer-item-dropdown.vue";
import InfoTooltip from "@/components/ui/tooltip/info-tooltip.vue";
import type { OfferCardData } from "./offer-card.vue";
import type { OfferGatingCriterionIcon } from "./use-offer-card";

defineProps<{
  offer: OfferCardData;
  ctaLabel: string;
  ctaClasses: string;
  ctaContentClass: string;
  cardAriaLabel: string;
  hasClaimed: boolean;
  isSoldOut: boolean;
  isGated: boolean;
  tooltipLabel: string;
  gatingCriterionIcons: OfferGatingCriterionIcon[];
  iconImageOverrides: Record<string, string>;
  canManageOffers: boolean;
}>();

const emit = defineEmits<{
  "cta-click": [];
  deleted: [];
}>();

const { t } = useI18n();
</script>
